import { useEffect, useRef, useState, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Sparkles, X, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STARTERS = [
  'What can Around the World AI actually build?',
  'Which plan is best for a solo founder?',
  'How much would I save vs DIY?',
  'Can I use my own OpenAI or Gemini key?',
];

function genSid() {
  return 'help-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/**
 * Signature "AI Concierge" widget — void-black + cyan/magenta brand theme.
 * Floating orb (wireframe globe) with cyan/magenta pulse, glass panel, and
 * Claude 4.5 streaming under the hood.
 */
export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem('atw_help_msgs');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem('atw_help_sid') || genSid()
  );
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('atw_help_sid', sessionId);
  }, [sessionId]);

  useEffect(() => {
    localStorage.setItem('atw_help_msgs', JSON.stringify(messages.slice(-40)));
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamedText, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = async (text) => {
    const clean = (text ?? input).trim();
    if (!clean || streaming) return;

    setMessages((m) => [...m, { role: 'user', content: clean }]);
    setInput('');
    setStreaming(true);
    setStreamedText('');

    try {
      const resp = await fetch(`${API}/support/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: clean }),
      });
      if (!resp.ok || !resp.body) throw new Error('Stream failed');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assembled = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const chunk of parts) {
          const line = chunk.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const evt = JSON.parse(payload);
            if (evt.type === 'delta') {
              assembled += evt.content;
              setStreamedText(assembled);
            }
          } catch {
            /* ignore */
          }
        }
      }
      setMessages((m) => [...m, { role: 'assistant', content: assembled }]);
      setStreamedText('');
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: 'Sorry, I could not reach the AI service. Please try again in a moment.',
        },
      ]);
    } finally {
      setStreaming(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setStreamedText('');
    setSessionId(genSid());
  };

  return (
    <>
      {/* Floating trigger — brand orb */}
      <button
        data-testid="assistant-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open AI concierge"
        className={`fixed z-[999] bottom-5 right-5 md:bottom-6 md:right-6 h-16 w-16 rounded-full flex items-center justify-center transition-all duration-300 ${
          open ? 'scale-90 rotate-45 opacity-70' : 'hover:scale-110'
        }`}
        style={{
          background:
            'radial-gradient(circle at 30% 30%, #1a1a2e 0%, #050510 60%, #000 100%)',
          boxShadow:
            '0 20px 40px -8px rgba(0,240,255,0.5), 0 0 0 1.5px rgba(0,240,255,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
        }}
      >
        {/* Pulse rings */}
        <span
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: 'rgba(0,240,255,0.28)', animationDuration: '2.5s' }}
        />
        <span
          className="absolute inset-[-3px] rounded-full pointer-events-none"
          style={{
            background:
              'conic-gradient(from 0deg, #00F0FF, #FF007F, #00F0FF)',
            filter: 'blur(6px)',
            opacity: open ? 0 : 0.5,
            transition: 'opacity 0.3s',
          }}
        />
        {open ? (
          <X size={22} className="text-white relative" strokeWidth={2.4} />
        ) : (
          <ConciergeMark />
        )}
      </button>

      {/* Panel */}
      <div
        className={`fixed z-[998] bottom-24 right-5 md:right-6 w-[360px] md:w-[400px] transition-all duration-300 origin-bottom-right ${
          open
            ? 'scale-100 opacity-100 pointer-events-auto'
            : 'scale-90 opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="rounded-3xl overflow-hidden glass-heavy"
          style={{
            background:
              'radial-gradient(600px 300px at 20% 0%, rgba(0,240,255,0.15) 0%, transparent 60%),' +
              'radial-gradient(600px 300px at 100% 100%, rgba(255,0,127,0.12) 0%, transparent 60%),' +
              'linear-gradient(180deg, rgba(3,3,3,0.96) 0%, rgba(10,10,20,0.98) 100%)',
            boxShadow:
              '0 40px 100px -30px rgba(0,240,255,0.35), 0 20px 60px -20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
        >
          {/* Header */}
          <div className="px-4 py-3.5 flex items-center gap-3 border-b border-white/8">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center relative"
              style={{
                background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
                boxShadow: '0 8px 20px -6px rgba(0,240,255,0.55)',
              }}
            >
              <ConciergeMark tiny />
            </div>
            <div className="flex-1">
              <div className="font-display text-white font-black text-[15px] leading-tight tracking-tight">
                Around The World<span className="bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">.AI</span>
              </div>
              <div className="text-[10px] text-white/60 flex items-center gap-1.5 mt-0.5 font-mono uppercase tracking-widest">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Concierge · Claude 4.5
              </div>
            </div>
            <button
              onClick={resetChat}
              data-testid="assistant-reset"
              className="h-8 w-8 rounded-lg inline-flex items-center justify-center text-white/50 hover:text-white hover:bg-white/8 transition"
              title="Start new chat"
            >
              <RotateCcw size={14} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="px-3 py-4 h-[380px] overflow-y-auto space-y-3"
            style={{ background: 'rgba(0,0,0,0.25)' }}
          >
            {messages.length === 0 && !streamedText && (
              <div className="px-2">
                <div className="flex items-start gap-2 mb-3">
                  <div
                    className="h-7 w-7 rounded-lg inline-flex items-center justify-center shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
                    }}
                  >
                    <Sparkles size={12} className="text-black" strokeWidth={2.5} />
                  </div>
                  <div className="rounded-2xl rounded-tl-sm glass px-3 py-2 text-[13.5px] text-white/85 leading-relaxed">
                    Hi! I&rsquo;m your <span className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">AI Concierge</span>.
                    Ask me anything about Around The World.AI &mdash; the platform, plans, or how to grow with us.
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      data-testid={`assistant-starter-${s.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => send(s)}
                      className="text-[11.5px] px-3 py-1.5 rounded-full glass text-white/80 hover:text-white hover:border-[#00F0FF]/40 transition"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}

            {streaming && <Bubble role="assistant" content={streamedText} streaming />}
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-white/8" style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="rounded-2xl glass focus-within:border-[#00F0FF]/50 focus-within:ring-1 focus-within:ring-[#00F0FF]/25 transition overflow-hidden">
              <Textarea
                ref={inputRef}
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask anything..."
                rows={1}
                className="min-h-[42px] resize-none border-0 focus-visible:ring-0 text-sm py-2.5 bg-transparent text-white placeholder:text-white/40"
              />
              <div className="flex items-center justify-between px-2.5 py-1.5 border-t border-white/8">
                <span className="text-[10px] text-white/40 font-mono uppercase tracking-widest">
                  Enter · send
                </span>
                <Button
                  onClick={() => send()}
                  data-testid="assistant-send"
                  disabled={streaming || !input.trim()}
                  size="sm"
                  className="h-7 rounded-lg text-black disabled:opacity-50 px-3 font-bold text-xs"
                  style={{
                    background: 'linear-gradient(90deg, #00F0FF 0%, #ffffff 50%, #FF007F 100%)',
                  }}
                >
                  <Send size={11} className="mr-1" /> Send
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** Miniature brand mark used inside the floating button */
function ConciergeMark({ tiny = false }) {
  const raw = useId();
  const uid = raw.replace(/[:]/g, 'x');
  const g = `cm-g-${uid}`;
  const r = `cm-r-${uid}`;
  const size = tiny ? 20 : 28;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className="relative">
      <defs>
        <linearGradient id={g} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00F0FF" />
          <stop offset="100%" stopColor="#FF007F" />
        </linearGradient>
        <linearGradient id={r} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00F0FF" stopOpacity="0" />
          <stop offset="50%" stopColor="#00F0FF" stopOpacity="1" />
          <stop offset="100%" stopColor="#FF007F" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Wireframe globe */}
      <g stroke={tiny ? '#0f172a' : '#e2e8f0'} strokeOpacity="0.85" strokeWidth={tiny ? 2 : 1.2} fill="none">
        <circle cx="32" cy="32" r="22" />
        <ellipse cx="32" cy="32" rx="22" ry="8" />
        <ellipse cx="32" cy="32" rx="8" ry="22" />
      </g>
      {/* Nodes */}
      <g fill="#00F0FF">
        <circle cx="22" cy="24" r="2" />
        <circle cx="42" cy="22" r="1.8" />
        <circle cx="26" cy="42" r="1.8" />
        <circle cx="44" cy="42" r="2" />
      </g>
      {/* Orbit ring */}
      <g transform="rotate(-25 32 32)">
        <ellipse cx="32" cy="32" rx="30" ry="9" fill="none" stroke={`url(#${r})`} strokeWidth="1.5" />
        <circle cx="2" cy="32" r="2.5" fill="#FF007F" />
      </g>
    </svg>
  );
}

function Bubble({ role, content, streaming }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`h-7 w-7 rounded-lg inline-flex items-center justify-center shrink-0 text-[10px] font-bold ${
          isUser ? 'text-white' : 'text-black'
        }`}
        style={
          isUser
            ? { background: 'linear-gradient(135deg, #0f172a, #1e293b)' }
            : { background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)' }
        }
      >
        {isUser ? 'You' : <Sparkles size={12} strokeWidth={2.5} />}
      </div>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13.5px] leading-relaxed ${
          isUser
            ? 'rounded-tr-sm text-white'
            : 'rounded-tl-sm text-white/85 glass'
        }`}
        style={
          isUser
            ? { background: 'linear-gradient(135deg, rgba(0,240,255,0.15), rgba(255,0,127,0.12))', border: '1px solid rgba(0,240,255,0.25)' }
            : {}
        }
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{content}</div>
        ) : content ? (
          <div className="assistant-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            {streaming && (
              <span
                className="inline-block w-1.5 h-3.5 align-middle ml-1 animate-pulse rounded-sm"
                style={{ background: '#00F0FF' }}
              />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 py-1">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        )}
      </div>
    </div>
  );
}
